import { Fragment } from 'preact';
import { forwardRef } from 'preact/compat';
import { useRef } from 'preact/hooks';

import { rnd } from '@/utils';
import { alwaysNotVisibleBlocks, honeyBotTypeName } from '@/constants';
import {
	Group,
	GroupTitle,
	TextInput,
	DropDown,
	Rating,
	SelectList,
	Link,
	Section,
	ExternalData,
	MatrixLikert,
	Screenshot,
	Contact,
	ErrorMessage,
	ReCaptchaWrapper,
	Multimedia,
	HoneyBot
} from '@/features/elements';
import useBlock from '../hooks/useBlock';
import { useConfigStore } from '../stores/ConfigStore';

const blockComponentMap = {
	textarea: TextInput,
	input: TextInput,
	radio: SelectList,
	checkbox: SelectList,
	select: DropDown,
	rating: Rating,
	nps: Rating,
	ces: Rating,
	gcr: SelectList,
	thumbs: SelectList,
	category: SelectList,
	contact: Contact,
	link: Link,
	likert: MatrixLikert,
	matrix: MatrixLikert,
	screenshot: Screenshot,
	website_data: ExternalData,
	section_break: Section,
	recaptcha: ReCaptchaWrapper,
	multimedia: Multimedia,
	[honeyBotTypeName]: HoneyBot
};

function ExtraElements({ state, block, actions, showErrors }) {
	const inputElement =
		block.properties.elements_extra[Object.keys(block.properties.elements_extra).pop()];

	return (
		<Fragment>
			<div
				style={{
					display: state[inputElement.data_field].isVisible ? 'block' : 'none',
					marginTop: 8
				}}
			>
				{['checkbox', 'radio', 'select'].indexOf(block.typeName) > -1 && (
					<TextInput
						block={block}
						state={state[inputElement.data_field]}
						setValue={value => actions.setNestedValue(inputElement.data_field, value)}
						showErrors={showErrors}
						placeholder={inputElement.placeholder}
					/>
				)}
			</div>
		</Fragment>
	);
}

export default function FormBlock({ id, showErrors, firstInvalid }) {
	const { text } = useConfigStore();
	const { state, block, actions, errorMessage, errorMessages } = useBlock({ id });

	const _id = useRef(rnd());

	if (block.typeName === 'screenshot' && block.properties?.bottomScreen) {
		return null;
	}

	const BlockComponent = blockComponentMap[block.typeName] ?? null;

	return (
		<Group
			block={block}
			focusInvalid={showErrors && firstInvalid}
			show={alwaysNotVisibleBlocks.indexOf(block.typeName) > -1 ? false : state.isVisible}
			typeCount={state.typeCount}
			_id={_id.current}
		>
			<GroupTitle
				required={block.properties?.required}
				block={block}
				requiredMarkLabel={errorMessage ?? text?.requiredMarkLabel}
				_id={_id.current}
			>
				{block.title}
			</GroupTitle>
			<BlockComponent
				block={block}
				state={state}
				{...actions}
				text={text}
				showErrors={showErrors}
				showError={state.showError}
				errorMessages={errorMessages}
				_id={_id.current}
			/>
			{block.properties?.elements_extra && (
				<ExtraElements
					block={block}
					state={state}
					actions={actions}
					showErrors={showErrors}
				/>
			)}
			{state.showError && block.typeName !== 'contact' && (
				<ErrorMessage
					id={`error-message-${block.id}`}
					showErrors={showErrors}
				>
					{errorMessage}
				</ErrorMessage>
			)}
		</Group>
	);
}
